// Public subcontractor onboarding page (no login). The one-time invite link the office
// emails lands here: the subcontractor fills in their details, answers "Do you employ anyone?",
// attaches whatever certificates they have, completes a short induction, and submits.
// No office nav, branded, mobile-first.
//
// SESSION AUR (the Director's rule): NOTHING is required except a phone number and the payment details. Every
// document, the insurance levels, the induction and the declaration may be left outstanding and never block
// submitting; the office follows them up. (Before AUR the form refused a registration with no competency document, and a
// subcontractor attached an unrelated photo just to get through.) The STATUS is no longer chosen here: it follows the
// certificates attached (signupSubcontractorStatus on the server, mirrored by statusFromCertificates below): Labour Only
// unless a Public Liability certificate is attached, plus an Employers Liability certificate for anyone who employs
// people; with them, Bona Fide pending ELE's approval. Every uploaded document is unverified until the office approves it.
//
// Design constraint: assume zero lift knowledge and zero code knowledge. Plain English.
// House rules: no double hyphens in copy, no personal names, the single client phone
// 0203 576 4760. Brand colours are CSS variables, so Tailwind alpha modifiers do not apply
// (use bg-black/20 + full-strength brand borders), matching the rest of the app shell.
//
// The induction questions + correct answers are mirrored on the server
// (server/src/modules/subcontractors/induction.ts) which re-validates every answer; keep
// the correct-answer letters in sync. Insurance minimums mirror server/src/lib (see
// src/lib/insuranceMinimums.ts).
//
// Session ARZ: a required "Payment details" section (PaymentDetailsFields, shared with the payment-details-only link).
// The rules and their wording mirror the server's (src/lib/subcontractorPayments.ts, parity-tested); the server
// re-checks everything and is the authority.
import { useEffect, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { SubcontractorOnboardingView } from "../../lib/api";
import { PUBLIC_LIABILITY_MINIMUM, EMPLOYERS_LIABILITY_MINIMUM } from "../../lib/insuranceMinimums";
import { WhatsAppLink } from "../../components/WhatsAppLink";
import { PrivacyNoticeLink } from "../../components/PrivacyNoticeLink";
import { PaymentDetailsFields, PaymentDetailsIntro } from "./PaymentDetailsFields";
import { EMPTY_PAYMENT_DETAILS, paymentDetailsErrors, type PaymentDetailsField, type PaymentDetailsForm } from "../../lib/subcontractorPayments";
import "./SubcontractorPages.css";

const PHONE = "0203 576 4760";
const LOGO = "/ELE_logo_new_reqt_FINALv2PNG.png";

const PL_MIN_M = PUBLIC_LIABILITY_MINIMUM / 1_000_000; // 5
const EL_MIN_M = EMPLOYERS_LIABILITY_MINIMUM / 1_000_000; // 10

// Competency document types (insurance certificates are uploaded in their own block).
const COMPETENCY_DOC_TYPES = [
  "CSCS Card",
  "NVQ Certificate",
  "IPAF Card",
  "AM2 Certificate",
  "City and Guilds Certificate",
  "Manufacturer Training Certificate",
  "Other",
] as const;

const PL_CERT_TYPE = "Public Liability Certificate";
const EL_CERT_TYPE = "Employer Liability Certificate";

// "Do you employ anyone?": unanswered, yes or no.
type EmploysAnswer = "" | "yes" | "no";

// Mirrors signupSubcontractorStatus (server/src/modules/subcontractors/subcontractorService.ts), which decides: a blank
// answer to "Do you employ anyone?" is read as needing Employers Liability too.
function statusFromCertificates(hasPl: boolean, hasEl: boolean, employs: EmploysAnswer): "bona_fide" | "labour_only" {
  if (!hasPl) return "labour_only";
  if (employs !== "no" && !hasEl) return "labour_only";
  return "bona_fide";
}

// Induction questions. The correct-answer letter is re-checked server-side. Copy uses no
// double hyphens (house rule), unlike the brief's draft wording.
interface InductionQuestion {
  id: "q1" | "q2" | "q3";
  prompt: string;
  options: { key: "a" | "b" | "c" | "d"; text: string }[];
  correct: "a" | "b" | "c" | "d";
}
const INDUCTION: InductionQuestion[] = [
  {
    id: "q1",
    prompt: "What do you do if you discover an unsafe condition on site?",
    correct: "b",
    options: [
      { key: "a", text: "Continue working and report it at the end of the day" },
      { key: "b", text: "Stop work immediately, make the area safe if possible, and report to ELE without delay" },
      { key: "c", text: "Fix it yourself without reporting it" },
      { key: "d", text: "Leave the site without informing anyone" },
    ],
  },
  {
    id: "q2",
    prompt: "Who is responsible for ensuring you hold the correct competency for the work you are undertaking?",
    correct: "c",
    options: [
      { key: "a", text: "The building owner" },
      { key: "b", text: "ELE's customer" },
      { key: "c", text: "You are personally responsible, and ELE will verify this" },
      { key: "d", text: "Nobody, competency is not required for lift work" },
    ],
  },
  {
    id: "q3",
    prompt: "What must you do before commencing any lift work on site?",
    correct: "b",
    options: [
      { key: "a", text: "Start work immediately to save time" },
      { key: "b", text: "Check in with ELE, review the job sheet, identify hazards, and confirm you have the correct tools and access" },
      { key: "c", text: "Wait for the building owner to give permission" },
      { key: "d", text: "Call ELE only if you encounter a problem" },
    ],
  },
];

const WORKING_STANDARDS =
  "Before you can be approved to work for East London Elevators Ltd, you must confirm that you have read and " +
  "understood ELE's working standards, health and safety requirements, and relevant risk assessments. These documents " +
  "are available in our compliance library and will be provided to you on request. By submitting this form you confirm " +
  "that you will work in accordance with ELE's instructions, risk assessments, and method statements at all times.";

interface DocBlock {
  docType: string;
  description: string;
  expiryDate: string;
  file: File | null;
}
const emptyCompetencyDoc = (): DocBlock => ({ docType: COMPETENCY_DOC_TYPES[0], description: "", expiryDate: "", file: null });

interface CertBlock {
  file: File | null;
  expiryDate: string;
}
const emptyCert = (): CertBlock => ({ file: null, expiryDate: "" });

export function SubcontractorOnboarding() {
  const { token = "" } = useParams();
  const [view, setView] = useState<SubcontractorOnboardingView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "expired" | "error" | null>(null);

  // Section 1: your details
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [address, setAddress] = useState("");
  const [tradingAddress, setTradingAddress] = useState("");
  const [companyNo, setCompanyNo] = useState("");
  const [utrNumber, setUtrNumber] = useState("");
  // Section 2: insurance (all optional; the certificates decide the status)
  const [employs, setEmploys] = useState<EmploysAnswer>("");
  const [publicLiabilityM, setPublicLiabilityM] = useState("");
  const [employerLiabilityM, setEmployerLiabilityM] = useState("");
  const [plCert, setPlCert] = useState<CertBlock>(emptyCert());
  const [elCert, setElCert] = useState<CertBlock>(emptyCert());
  // Section 4: competency docs
  const [docs, setDocs] = useState<DocBlock[]>([emptyCompetencyDoc()]);
  // Section 5: induction
  const [answers, setAnswers] = useState<Record<string, string>>({ q1: "", q2: "", q3: "" });
  // Section 6: declaration
  const [declared, setDeclared] = useState(false);
  // Payment details (Session ARZ)
  const [payment, setPayment] = useState<PaymentDetailsForm>(EMPTY_PAYMENT_DETAILS);
  const [paymentErrors, setPaymentErrors] = useState<Partial<Record<PaymentDetailsField, string>>>({});

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.publicSubcontractor
      .get(token)
      .then((v) => {
        if (!live) return;
        setView(v);
        setContactName(v.contactName);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 404) setLoadError("not_found");
        else if (err instanceof ApiError && err.status === 410) setLoadError("expired");
        else setLoadError("error");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [token]);

  const attachedCompetencyDocs = docs.filter((d) => d.file);

  function setDoc(i: number, patch: Partial<DocBlock>) {
    setDocs((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function addDoc() {
    setDocs((prev) => [...prev, emptyCompetencyDoc()]);
  }
  function removeDoc(i: number) {
    setDocs((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  }
  function onDocFile(i: number, e: ChangeEvent<HTMLInputElement>) {
    setDoc(i, { file: e.target.files?.[0] ?? null });
  }

  // The status these certificates would give (shown to the subcontractor before they submit).
  const elApplies = employs === "yes";
  const resultingStatus = statusFromCertificates(!!plCert.file, elApplies && !!elCert.file, employs);

  // Session AUR: only the phone number and the payment details block a submission. Mirrored on the server, which
  // re-checks both and is the authority.
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!contactPhone.trim()) e.contactPhone = "Please enter a contact phone number.";
    const pe = paymentDetailsErrors(payment);
    setPaymentErrors(pe);
    if (Object.keys(pe).length > 0) e.paymentDetails = "Please check your payment details.";
    return e;
  }

  async function onSubmit() {
    if (submitting) return;
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSubmitError("Please correct the highlighted items and try again.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const form = new FormData();
      form.append("contactName", contactName);
      form.append("contactPhone", contactPhone);
      form.append("address", address);
      form.append("tradingAddress", tradingAddress);
      form.append("companyNo", companyNo);
      form.append("utrNumber", utrNumber);
      form.append("declarationConfirmed", String(declared));
      form.append("inductionQ1", answers.q1);
      form.append("inductionQ2", answers.q2);
      form.append("inductionQ3", answers.q3);
      form.append("bankAccountName", payment.accountName);
      form.append("bankSortCode", payment.sortCode);
      form.append("bankAccountNumber", payment.accountNumber);
      form.append("bankAccountNumberConfirm", payment.accountNumberConfirm);
      form.append("employsStaff", employs === "yes" ? "true" : employs === "no" ? "false" : "");
      if (Number(publicLiabilityM) > 0) form.append("publicLiabilityHeld", String(Math.round(Number(publicLiabilityM) * 1_000_000)));
      if (elApplies && Number(employerLiabilityM) > 0) {
        form.append("employerLiabilityHeld", String(Math.round(Number(employerLiabilityM) * 1_000_000)));
      }

      // Build the combined indexed document list: competency docs first, then the insurance certificates (whichever are
      // attached) as their own typed docs. None is required.
      const allDocs: DocBlock[] = [...attachedCompetencyDocs];
      if (plCert.file) allDocs.push({ docType: PL_CERT_TYPE, description: "", expiryDate: plCert.expiryDate, file: plCert.file });
      if (elApplies && elCert.file) allDocs.push({ docType: EL_CERT_TYPE, description: "", expiryDate: elCert.expiryDate, file: elCert.file });
      allDocs.forEach((d, i) => {
        form.append(`docFile_${i}`, d.file as File, (d.file as File).name);
        form.append(`docType_${i}`, d.docType);
        form.append(`docDescription_${i}`, d.description);
        form.append(`docExpiry_${i}`, d.expiryDate);
      });

      await api.publicSubcontractor.submit(token, form);
      setDone(true);
    } catch (err) {
      const field = err instanceof ApiError ? err.body?.field : undefined;
      if (err instanceof ApiError && err.status === 410) {
        setLoadError("expired");
      } else if (err instanceof ApiError && typeof field === "string" && field in EMPTY_PAYMENT_DETAILS) {
        // The server refused a payment detail the form's own check let through: mark that field.
        setPaymentErrors({ [field as PaymentDetailsField]: err.code });
        setSubmitError("Please check your payment details.");
      } else if (err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")) {
        setSubmitError(err.code);
      } else {
        setSubmitError(`Something went wrong submitting your registration. Please try again, or call us on ${PHONE}.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <p className="text-center" style={{ color: "var(--sp-ink-muted)" }}>
          Loading...
        </p>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div className="sp-state-box -error">
          <h2>
            {loadError === "not_found" ? "This link is not valid" : loadError === "expired" ? "This link has expired" : "Something went wrong"}
          </h2>
          <p>
            {loadError === "expired"
              ? "This link has expired or has already been used. Please contact East London Elevators on 0203 576 4760 and we will send a fresh one."
              : loadError === "not_found"
                ? "Please check the link in your email, or contact East London Elevators and we will help."
                : "Please try again in a moment, or contact East London Elevators and we will help."}
          </p>
          <p className="mt-4 text-sm font-semibold" style={{ color: "var(--sp-blue)" }}>
            Call us on {PHONE}
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="sp-state-box -done">
          <div aria-hidden="true" className="sp-tick">
            {"✓"}
          </div>
          <h2>Thank you</h2>
          <p>
            Your registration has been submitted to East London Elevators Ltd. We will review your details and be in
            touch. For any queries call {PHONE}.
          </p>
          <p className="mt-2">Before we make your first payment, we will phone you to confirm your payment details.</p>
        </div>
      </Shell>
    );
  }

  if (!view) return null;

  return (
    <Shell>
      <header className="mb-6">
        <p className="sp-eyebrow">Subcontractor registration</p>
        <h1 className="sp-title">{view.company}</h1>
        <p className="sp-lead">
          Please complete the short form below so we can add you to our approved subcontractor list. It takes a few
          minutes. Only your phone number and payment details are needed now: anything else, including your documents,
          can follow later.
        </p>
      </header>

      {/* 1. Your details */}
      <Section title="Your details">
        <div className="grid gap-3 sm:grid-cols-2">
          <ReadOnly label="Company" value={view.company} />
          <ReadOnly label="Email" value={view.contactEmail} />
          <Field label="Contact name" value={contactName} onChange={setContactName} />
          <Field label="Contact phone" required value={contactPhone} onChange={setContactPhone} error={fieldErrors.contactPhone} />
          <Field label="Trade" value={view.trade || "Not set"} onChange={() => undefined} readOnly />
          <div />
          <Field label="Company number" value={companyNo} onChange={setCompanyNo} />
          <Field label="UTR number" value={utrNumber} onChange={setUtrNumber} />
        </div>
        <p className="mt-1 text-xs" style={{ color: "var(--sp-ink-faint)" }}>
          Enter your Company Number if you are a limited company, or your UTR number if you are a sole trader.
        </p>
        <div className="mt-3">
          <Field label="Registered address" value={address} onChange={setAddress} />
        </div>
        <div className="mt-3">
          <Field label="Trading address (if different from registered address)" value={tradingAddress} onChange={setTradingAddress} />
        </div>
      </Section>

      {/* 2. Insurance (optional). The certificates attached decide the status; nothing here is required. */}
      <Section title="Insurance">
        <p className="sp-sec-note">
          Optional: you can register without these and send them to us later. To work as a Bona Fide subcontractor we need
          your Public Liability certificate (minimum £{PL_MIN_M} million), and, if you employ anyone, your Employers Liability
          certificate (minimum £{EL_MIN_M} million). Without them you are registered as Labour Only, working under ELE's
          direction and covered by ELE's insurance, until we have received and approved them. Every certificate is checked by
          ELE before it counts.
        </p>
        <fieldset>
          <legend className="sp-label mb-2">Do you employ anyone?</legend>
          <RadioRow name="employs" checked={employs === "yes"} onChange={() => setEmploys("yes")} label="Yes, I employ staff or operatives" />
          <RadioRow name="employs" checked={employs === "no"} onChange={() => setEmploys("no")} label="No, I work on my own" />
        </fieldset>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field
            label="Public Liability level held (£ millions)"
            value={publicLiabilityM}
            onChange={setPublicLiabilityM}
            type="number"
            inputMode="numeric"
          />
          <div />
        </div>
        {Number(publicLiabilityM) > 0 && Number(publicLiabilityM) < PL_MIN_M && (
          <WarnText>
            The Public Liability level you entered (£{publicLiabilityM} million) is below ELE's minimum of £{PL_MIN_M}{" "}
            million. You can still submit; ELE will review this.
          </WarnText>
        )}
        {elApplies && (
          <div className="mt-3">
            <Field
              label="Employers Liability level held (£ millions)"
              value={employerLiabilityM}
              onChange={setEmployerLiabilityM}
              type="number"
              inputMode="numeric"
            />
            {Number(employerLiabilityM) > 0 && Number(employerLiabilityM) < EL_MIN_M && (
              <WarnText>
                The Employers Liability level you entered (£{employerLiabilityM} million) is below ELE's minimum of £
                {EL_MIN_M} million. You can still submit; ELE will review this.
              </WarnText>
            )}
          </div>
        )}

        <h3 className="sp-label mt-5 mb-2" style={{ fontSize: "12px" }}>
          Insurance documents (optional)
        </h3>
        <CertUpload
          label="Public Liability Certificate"
          cert={plCert}
          onFile={(f) => setPlCert((p) => ({ ...p, file: f }))}
          onExpiry={(v) => setPlCert((p) => ({ ...p, expiryDate: v }))}
        />
        {elApplies && (
          <CertUpload
            label="Employers Liability Certificate"
            cert={elCert}
            onFile={(f) => setElCert((p) => ({ ...p, file: f }))}
            onExpiry={(v) => setElCert((p) => ({ ...p, expiryDate: v }))}
          />
        )}
        <p className="sp-sec-note mt-2" data-testid="status-preview">
          {resultingStatus === "bona_fide"
            ? "With these certificates you will be registered as Bona Fide, pending ELE's approval of your certificates."
            : "You will be registered as Labour Only until we have received and approved your insurance certificates."}
        </p>
      </Section>

      {/* 4. Competency documents */}
      <Section title="Competency documents">
        <p className="sp-sec-note">
          Optional: attach any document showing your competency (for example a CSCS card, NVQ, or IPAF card), or send it
          to us later. Accepted formats: PDF, JPG, PNG.
        </p>
        <div className="space-y-4">
          {docs.map((d, i) => (
            <div key={i} className="sp-panel">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Document type</Label>
                  <select
                    value={d.docType}
                    aria-label={`Document ${i + 1} type`}
                    onChange={(e) => setDoc(i, { docType: e.target.value })}
                  >
                    {COMPETENCY_DOC_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <Field label="Description (optional)" value={d.description} onChange={(v) => setDoc(i, { description: v })} />
                <Field label="Expiry date (optional)" value={d.expiryDate} onChange={(v) => setDoc(i, { expiryDate: v })} type="date" />
                <div>
                  <Label>File</Label>
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                    aria-label={`Document ${i + 1} file`}
                    onChange={(e) => onDocFile(i, e)}
                  />
                </div>
              </div>
              {docs.length > 1 && (
                <button type="button" onClick={() => removeDoc(i)} className="sp-btn-remove mt-2">
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={addDoc} className="sp-btn-ghost mt-3">
          Add another document
        </button>
      </Section>

      {/* Payment details (Session ARZ): required, so ELE can pay the subcontractor's invoices. */}
      <Section title="Payment details">
        <PaymentDetailsIntro />
        <PaymentDetailsFields value={payment} onChange={setPayment} errors={paymentErrors} />
      </Section>

      {/* 5. ELE working standards + induction */}
      <Section title="ELE Working Standards">
        <p className="sp-sec-note">{WORKING_STANDARDS}</p>
        <p className="sp-sec-note">
          If you would rather go through these questions with ELE, leave them and submit: they do not stop you registering.
        </p>
        <div className="space-y-5">
          {INDUCTION.map((q, qi) => (
            <fieldset key={q.id}>
              <legend className="mb-2 text-sm font-semibold" style={{ color: "var(--sp-ink-strong)" }}>
                {qi + 1}. {q.prompt}
              </legend>
              <div className="space-y-2">
                {q.options.map((o) => (
                  <RadioRow
                    key={o.key}
                    name={q.id}
                    checked={answers[q.id] === o.key}
                    onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: o.key }))}
                    label={o.text}
                  />
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </Section>

      {/* 6. Declaration */}
      <label className="sp-option mt-2">
        <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} className="mt-1" aria-label="Declaration" />
        <span>
          I confirm that all information provided is accurate and complete. I understand that providing false information
          may result in my removal from ELE's approved subcontractor register.
        </span>
      </label>

      {submitError && (
        <p role="alert" aria-live="assertive" className="sp-error-box">
          {submitError}
        </p>
      )}

      <button type="button" onClick={onSubmit} disabled={submitting} className="sp-btn-primary mt-5 sm:w-auto sm:px-10">
        {submitting ? "Submitting..." : "Submit registration"}
      </button>

      <Footer />
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="subpage">
      <div className="sp-hdr">
        <img src={LOGO} alt="East London Elevators" />
        <div className="sp-hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
        </div>
      </div>
      <div className="sp-wrap">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="sp-sec">
      <h2 className="sp-sec-title">{title}</h2>
      {children}
    </section>
  );
}

function Label({ children }: { children: ReactNode }) {
  return <label className="sp-label">{children}</label>;
}

function ErrorText({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="sp-error-text">
      {children}
    </p>
  );
}

function WarnText({ children }: { children: ReactNode }) {
  return <p className="sp-warn-box">{children}</p>;
}

function RadioRow({ name, checked, onChange, label }: { name: string; checked: boolean; onChange: () => void; label: string }) {
  return (
    <label className="sp-option">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1" aria-label={label} />
      <span>{label}</span>
    </label>
  );
}

function CertUpload({
  label,
  required,
  cert,
  onFile,
  onExpiry,
  error,
}: {
  label: string;
  required?: boolean;
  cert: CertBlock;
  onFile: (f: File | null) => void;
  onExpiry: (v: string) => void;
  error?: string;
}) {
  return (
    <div className="sp-panel mb-3">
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          type="file"
          accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
          aria-label={`${label} file`}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <div>
          <Label>Expiry date (optional)</Label>
          <input
            type="date"
            value={cert.expiryDate}
            aria-label={`${label} expiry date`}
            onChange={(e) => onExpiry(e.target.value)}
          />
        </div>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  inputMode,
  readOnly,
  required,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: "numeric";
  readOnly?: boolean;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <input
        type={type}
        value={value}
        inputMode={inputMode}
        readOnly={readOnly}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="sp-panel" style={{ padding: "9px 12px", fontSize: "14px", color: "var(--sp-ink-muted)" }}>
        {value}
      </div>
    </div>
  );
}

function Footer() {
  // Session ASQ (Stream G, Item 5): the dimming moved from the footer onto its lines, so the privacy notice link (shown
  // only once the notice is published) keeps its full contrast on this dark canvas. The existing lines read as before.
  return (
    <footer className="sp-footer">
      <p>For and on behalf of East London Elevators Ltd.</p>
      <p>East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
      <p>
        <WhatsAppLink />
      </p>
      <PrivacyNoticeLink className="mt-2" color="var(--sp-purple)" />
    </footer>
  );
}
